import React, { useState } from 'react';
import Button from '../../../components/ui/Button';

const ProductTable = ({
  products = [],
  loading = false,
  onEdit,
  onDelete,
  selectedProducts = [],
  onSelectProduct,
  onSelectAll,
  isGridView = false
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

  // Sorting by product_name, sku_code, brand_name, category_name
  const sortedProducts = React.useMemo(() => {
    if (!Array.isArray(products)) return [];
    if (!sortConfig?.key) return products;
    return [...products]?.sort((a, b) => {
      if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [products, sortConfig]);

  const isAllSelected = products?.length > 0 && selectedProducts?.length === products?.length;
  const isIndeterminate = selectedProducts?.length > 0 && selectedProducts?.length < products?.length;

  // Helper for category levels
  const renderCategoryLevels = (product) => (
    <div className="space-y-0.5">
      {product?.primary_category_name && (
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold">Prim Category:</span> {product.primary_category_name}
        </div>
      )}
      {product?.category_name && (
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold">Category:</span> {product.category_name}
        </div>
      )}
      {product?.sub_category_name && (
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold">Sub Category:</span> {product.sub_category_name}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Grid/List View */}
      {isGridView ? (
        // Grid View
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground text-base">Loading...</div>
          ) : sortedProducts?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-muted-foreground text-2xl">🛒</span>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No products found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your filters or search.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedProducts?.map((product) => (
                <div
                  key={product?.product_id}
                  className="bg-muted/20 border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Product Image */}
                  <div className="relative bg-muted h-48 flex items-center justify-center">
                    <img
                      src={product?.images?.image1}
                      alt={product?.product_name}
                      className="w-full h-full object-cover"
                    />
                    {/* Checkbox overlay */}
                    <div className="absolute top-2 left-2 bg-white rounded border border-border">
                      <input
                        type="checkbox"
                        checked={selectedProducts?.includes(product?.product_id)}
                        onChange={(e) => onSelectProduct(product?.product_id, e?.target?.checked)}
                        className="p-1 rounded border-border focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="p-4 space-y-3">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{product?.category_name}</div>
                      <h3 className="text-sm font-semibold text-foreground line-clamp-2">
                        {capitalize(product?.product_name)}
                      </h3>
                    </div>

                    {/* Brand and Code */}
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">
                        <span className="font-semibold">Brand:</span> {capitalize(product?.brand_name)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-semibold">Code:</span> {product?.sku_code}
                      </div>
                    </div>

                    {/* Category Levels */}
                    {(product?.primary_category_name || product?.sub_category_name) && (
                      <div className="space-y-0.5 border-t border-border pt-2">
                        {product?.primary_category_name && (
                          <div className="text-xs text-muted-foreground">
                            <span className="font-semibold">Prim:</span> {product.primary_category_name}
                          </div>
                        )}
                        {product?.sub_category_name && (
                          <div className="text-xs text-muted-foreground">
                            <span className="font-semibold">Sub:</span> {product.sub_category_name}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center space-x-2 pt-2 border-t border-border">
                      <Button
                        variant="default"
                        size="xs"
                        iconName="Edit"
                        onClick={() => onEdit(product)}
                        className="flex-1"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Trash2"
                        onClick={() => onDelete(product)}
                        className="text-error hover:text-error"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // List View (Table)
        <>
        <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  className="rounded border-border focus:ring-2 focus:ring-ring"
                />
              </th>
              <th className="px-4 py-3 text-sm font-medium text-foreground">
                Image
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                <button
                  onClick={() => handleSort('category_name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span className="text-sm font-medium">Category</span>
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                <button
                  onClick={() => handleSort('product_name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span className="text-sm font-medium">Product Name</span>
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                <button
                  onClick={() => handleSort('sku_code')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span className="text-sm font-medium">Product Code</span>
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                <button
                  onClick={() => handleSort('brand_name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span className="text-sm font-medium">Brand</span>
                </button>
              </th>
              <th className="text-right px-4 py-3 text-sm font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-muted-foreground text-base">Loading...</td>
              </tr>
            ) : sortedProducts?.map((product) => (
              <tr key={product?.product_id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedProducts?.includes(product?.product_id)}
                    onChange={(e) => onSelectProduct(product?.product_id, e?.target?.checked)}
                    className="rounded border-border focus:ring-2 focus:ring-ring"
                  />
                </td>
                <td className="px-4 py-3">
                  <img
                    src={product?.images?.image1}
                    alt={product?.product_name}
                    width={60}
                    height={60}
                    className="object-cover rounded border border-border"
                    style={{ width: 60, height: 60 }}
                  />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <div className="text-sm font-medium text-foreground">{product?.category_name}</div>
                    {renderCategoryLevels(product)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">
                    {capitalize(product?.product_name)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{product?.sku_code}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{capitalize(product?.brand_name)}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="xs"
                      iconName="Edit"
                      onClick={() => onEdit(product)}
                      title="Edit product"
                    />
                    {/* <Button
                      variant="ghost"
                      size="xs"
                      iconName="Trash2"
                      onClick={() => onDelete(product)}
                      title="Delete product"
                      className="text-error hover:text-error"
                    /> */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile/Tablet Cards */}
      <div className="lg:hidden divide-y divide-border">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-base">Loading...</div>
        ) : sortedProducts?.map((product) => (
          <div key={product?.product_id} className="p-4 space-y-3">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={selectedProducts?.includes(product?.product_id)}
                onChange={(e) => onSelectProduct(product?.product_id, e?.target?.checked)}
                className="mt-1 rounded border-border focus:ring-2 focus:ring-ring"
              />
              <img
                src={product?.images?.image1}
                alt={product?.product_name}
                width={60}
                height={60}
                className="object-cover rounded border border-border"
                style={{ width: 60, height: 60 }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{product?.category_name}</div>
                {renderCategoryLevels(product)}
                <div className="text-sm font-medium text-foreground mt-1">
                  {capitalize(product?.product_name)}
                </div>
                <div className="text-xs text-muted-foreground">Code: {product?.sku_code}</div>
                <div className="text-xs text-muted-foreground">Brand: {capitalize(product?.brand_name)}</div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-1 pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="xs"
                iconName="Edit"
                onClick={() => onEdit(product)}
              />
              <Button
                variant="ghost"
                size="xs"
                iconName="Trash2"
                onClick={() => onDelete(product)}
                className="text-error hover:text-error"
              />
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {!loading && products?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-muted-foreground text-2xl">🛒</span>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No products found</h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your filters or search.
          </p>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ProductTable;